import React, { useState } from "react";
import { Input, Space, Table, Tag, Button } from "antd";
import { useQuery } from "react-query";
import { useIntl } from "react-intl";
import jwtAxios from "../../../../auth/jwt-api";
import useDebounce from "../../../../utils/useDebounce";

const TYPE_COLORS = {
  sold: "blue",
  not_full_paid: "orange",
  reserved_paid: "gold",
  mortgage: "purple",
  reserved: "cyan",
};

export default function DeleteOrders() {
  const { formatMessage } = useIntl();
  const t = (id) => formatMessage({ id });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
  });

  const [searchClient, setSearchClient] = useState("");
  const [searchContract, setSearchContract] = useState("");

  const debouncedSetClient = useDebounce((value) => {
    setSearchClient(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, 500);

  const debouncedSetContract = useDebounce((value) => {
    setSearchContract(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, 500);

  const { data, isLoading } = useQuery(
    ["/order-deleted", pagination.current, pagination.pageSize, searchClient, searchContract],
    () =>
      jwtAxios.get("/order", {
        params: {
          page: pagination.current,
          limit: pagination.pageSize,
          order: "createdAt",
          sortBy: "DESC",
          deleted: true,
          ...(searchClient && { clientName: searchClient }),
          ...(searchContract && { contractNumber: searchContract }),
        },
      }),
    { keepPreviousData: true }
  );

  const rows = data?.data?.data?.results?.rows ?? [];
  const total = data?.data?.data?.results?.count ?? 0;

  const getObject = (record) => {
    if (record.apartment) {
      return {
        label: t("order.object.apartment"),
        number:
          record.apartment.apartment_number ?? record.apartment.number ?? "-",
      };
    }
    if (record.parking) {
      return {
        label: t("order.object.parking"),
        number: record.parking.number ?? "-",
      };
    }
    if (record.storage) {
      return {
        label: t("order.object.storage"),
        number: record.storage.number ?? "-",
      };
    }
    return { label: t("order.empty"), number: t("order.empty") };
  };

  const formatPrice = (value, currency) => {
    if (value == null) return "-";
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return `${num.toLocaleString("ru-RU")} ${
      currency ? currency.toUpperCase() : ""
    }`.trim();
  };

  const columns = [
    {
      title: t("order.field.id"),
      dataIndex: "id",
      key: "id",
      width: 70,
    },
    {
      title: t("order.field.client"),
      key: "client",
      render: (_, record) => record.client?.customerName ?? t("order.empty"),
    },
    {
      title: t("order.field.phone"),
      key: "phone",
      render: (_, record) => record.client?.customerPhone ?? t("order.empty"),
    },
    {
      title: t("order.field.object"),
      key: "object",
      render: (_, record) => {
        const { label, number } = getObject(record);
        return `${label} № ${number}`;
      },
    },
    {
      title: t("order.field.contractNumber"),
      key: "contractNumber",
      render: (_, record) =>
        record.contracts?.[0]?.contractNumber ?? t("order.empty"),
    },
    {
      title: t("order.field.signature"),
      key: "signature",
      render: (_, record) =>
        record.contracts?.[0]?.my_signature ? (
          <Tag color="green">{t("order.signature.signed")}</Tag>
        ) : (
          <Tag color="red">{t("order.signature.unsigned")}</Tag>
        ),
    },
    {
      title: t("order.field.price"),
      key: "price",
      render: (_, record) => formatPrice(record.price, record.priceCurrency),
    },
    {
      title: t("order.field.price_m2"),
      key: "price_m2",
      render: (_, record) =>
        formatPrice(record.price_m2, record.priceCurrency),
    },
    {
      title: t("order.field.discount"),
      dataIndex: "discount",
      key: "discount",
      render: (value) => (value != null ? `${value}%` : t("order.empty")),
    },
    {
      title: t("order.field.downPaymentPercent"),
      dataIndex: "downPaymentPercent",
      key: "downPaymentPercent",
      render: (value) => (value != null ? `${value}%` : t("order.empty")),
    },
    {
      title: t("order.field.paymentDurationPerMonth"),
      dataIndex: "paymentDurationPerMonth",
      key: "paymentDurationPerMonth",
      render: (value) => value ?? t("order.empty"),
    },
    {
      title: t("order.field.type"),
      dataIndex: "type",
      key: "type",
      render: (value) => {
        if (!value) return <Tag>{t("order.empty")}</Tag>;
        const label = formatMessage({
          id: `order.type.${value}`,
          defaultMessage: value,
        });
        return <Tag color={TYPE_COLORS[value] ?? "default"}>{label}</Tag>;
      },
    },
    {
      title: t("order.field.contractType"),
      key: "contractType",
      render: (_, record) => {
        const contract = record.contracts?.[0];
        const isOffline = !!contract?.contract_file_offline;
        return (
          <Tag color={isOffline ? "orange" : "green"}>
            {t(isOffline ? "order.field.contractType.offline" : "order.field.contractType.online")}
          </Tag>
        );
      },
    },
    {
      title: t("order.field.contract"),
      key: "contract",
      render: (_, record) => {
        const contract = record.contracts?.[0];
        const fileUrl = `${process.env.REACT_APP_API_URL}/${contract?.contract_file_offline ? contract?.contract_file_offline : contract?.contract_file}`;
        if (!fileUrl) return t("order.empty");
        return (
          <Button
            type="link"
            href={fileUrl}
            target="_blank"
            download
          >
            {t("button.download")}
          </Button>
        );
      },
    },
    {
      title: t("order.field.user"),
      key: "user",
      render: (_, record) => record.user?.username ?? t("order.empty"),
    },
    {
      title: t("order.field.createdAt"),
      dataIndex: "createdAt",
      key: "createdAt",
      render: (value) =>
        value ? new Date(value).toLocaleString() : t("order.empty"),
    },
  ];

  return (
    <div style={{ padding: "clamp(10px,3%,30px)" }}>
      <h1>{t("order.deleted.page.title")}</h1>
      <br />
      <Space style={{ marginBottom: 16 }} wrap>
        <div>
          <label>{t("order.field.client")}</label>
          <Input
            placeholder={t("order.field.client")}
            allowClear
            onChange={(e) => debouncedSetClient(e.target.value)}
            style={{ width: 250 }}
          />
        </div>
        <div>
          <label>{t("order.field.contractNumber")}</label>
          <Input
            placeholder={t("order.field.contractNumber")}
            allowClear
            onChange={(e) => debouncedSetContract(e.target.value)}
            style={{ width: 250 }}
          />
        </div>
      </Space>
      <Table
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={rows}
        scroll={{ x: "max-content" }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total,
          showSizeChanger: true,
        }}
        onChange={(p) =>
          setPagination({ current: p.current, pageSize: p.pageSize })
        }
      />
    </div>
  );
}